// 설정 > 계정 — PW 변경 + 복구 코드 회전 + 표시 설정(밀도 모드).

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { useToast } from '../components/Toast';
import { isApiError, formatApiError } from '../api/client';
import { CopyButton } from '../components/CopyButton';
import { DensityPicker } from '../components/DensityPicker';
import { useDensity } from '../hooks/useDensity';

export default function SettingsAccount() {
  const toast = useToast();

  return (
    <div className="stack">
      <h2>설정 · 계정</h2>
      <DisplaySettings />
      <ChangePassword />
      <RotateRecovery />
      <TotpResetSection />
    </div>
  );

  function _unused() { return toast; }
}

// 표시 설정 — 밀도 모드 토글
function DisplaySettings() {
  const { density, setDensity } = useDensity();

  return (
    <section className="card stack">
      <h3>표시 설정</h3>
      <p className="muted">화면 밀도를 선택하면 목록 간격·글자 크기가 즉시 반영됩니다.</p>
      <DensityPicker value={density} onChange={setDensity} />
    </section>
  );
}

function ChangePassword() {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const toast = useToast();
  const mut = useMutation({
    mutationFn: ({ oldPw, newPw }: { oldPw: string; newPw: string }) =>
      authApi.changePassword(oldPw, newPw),
    onSuccess: () => {
      toast.success('패스워드 변경 완료');
      setOldPw(''); setNewPw(''); setConfirm('');
    },
    onError: (err) => {
      const msg = isApiError(err) && err.error === 'invalid_credentials'
        ? '현재 패스워드가 올바르지 않습니다'
        : formatApiError(err, '변경 실패');
      toast.error(msg);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('새 패스워드는 8자 이상'); return; }
    if (newPw !== confirm) { toast.error('확인이 일치하지 않습니다'); return; }
    mut.mutate({ oldPw, newPw });
  };

  return (
    <section className="card stack">
      <h3>마스터 패스워드 변경</h3>
      <p className="muted">변경 후에도 데이터·TOTP는 보존됩니다.</p>
      <form onSubmit={onSubmit} className="stack">
        <label><span>현재 패스워드</span>
          <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} required />
        </label>
        <label><span>새 패스워드 (8자 이상)</span>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required />
        </label>
        <label><span>확인</span>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </label>
        <button type="submit" className="btn btn--primary" disabled={mut.isPending}>
          {mut.isPending ? '변경 중…' : '변경'}
        </button>
      </form>
    </section>
  );
}

function RotateRecovery() {
  const [newCode, setNewCode] = useState<string | null>(null);
  const toast = useToast();
  const mut = useMutation({
    mutationFn: () => authApi.rotateRecovery(),
    onSuccess: (r) => { setNewCode(r.newRecoveryCode); toast.success('복구 코드 갱신 완료'); },
    onError: (err) => toast.error(formatApiError(err, '갱신 실패')),
  });

  const onClick = () => {
    if (!window.confirm('이전 복구 코드는 즉시 무효화됩니다. 계속하시겠어요?')) return;
    mut.mutate();
  };

  return (
    <section className="card stack">
      <h3>복구 코드 재발급</h3>
      {newCode ? (
        <div className="callout callout--warn stack">
          <p><b>새 복구 코드</b> — 안전한 곳에 저장하세요. 화면을 닫으면 다시 볼 수 없습니다.</p>
          <div className="post-init__code">
            <code>{newCode}</code>
            <CopyButton value={newCode} />
          </div>
          <button type="button" className="btn btn--ghost" onClick={() => setNewCode(null)}>저장했습니다</button>
        </div>
      ) : (
        <>
          <p className="muted">기존 복구 코드를 분실했거나 유출이 의심될 때 사용하세요.</p>
          <button type="button" className="btn btn--primary" onClick={onClick} disabled={mut.isPending}>
            {mut.isPending ? '처리 중…' : '새 복구 코드 발급'}
          </button>
        </>
      )}
    </section>
  );
}

function TotpResetSection() {
  return (
    <section className="card stack">
      <h3>TOTP 재설정</h3>
      <p className="muted">Phase 2에서 제공 예정입니다. 현재는 인증기 변경 시 마스터 PW 재설정(복구 코드 사용)으로 우회하세요.</p>
      <button type="button" className="btn btn--ghost" disabled>준비 중</button>
    </section>
  );
}
